import { useProperty } from '../../Framework/View/index.js'
import { useEffect, useState } from 'react'
import { useGetAllOrders } from '../../Flux/Selector/index.js'
import { useInteraction } from '../../Framework/View/Hooks/useInteraction.js'
import { debounceTime } from 'rxjs'
import Actions from '../../Flux/index.js'
import { orderSlice } from '../../Flux/Slice/Order/OrderSlice.js'
import { useDispatch } from 'react-redux'
import { FormControl, Input, Pagination, Select } from '../Common/index.ts'
import { takeOptions } from '../Page/OrderManagementPage/OrderManagementPage.tsx'
import type { IOrder } from '../../Model/index.ts'
import { getIconFromTaxa } from '../../Util/iconUtil.tsx'
import { taxaOptions } from '../Page/HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import type { Taxas } from '../../Model/ITaxa.ts'
import { FilterTypes } from '../../Generated/FilterType.js'
import { ProcessingTypeBadge } from '../Common/ProcessingTypeBadge.tsx'

type OrderSearchWidgetProps = {
  onSelect: (order: IOrder) => void
  initialFilters?: {
    taxa?: Taxas | null
    processingType?: string | null
  }
}

export const OrderSearchWidget = ({ onSelect, initialFilters }: OrderSearchWidgetProps) => {
  const search$ = useProperty<string>('')
  const take$ = useProperty<string>('10')
  const [page, setPage] = useState<number>(1)
  const [onlyCompleted, setOnlyCompleted] = useState<boolean>(false)
  const [onlySubmitted, setOnlySubmitted] = useState<boolean>(false)
  const [processingTypeFilter, setProcessingTypeFilter] = useState<string | null>(initialFilters?.processingType ?? null)
  const { orders, total } = useGetAllOrders()
  const onPagination$ = useInteraction<number>()
  const [taxaFilter, setTaxaFilter] = useState<string | null>(initialFilters?.taxa ?? null)
  const dispatch = useDispatch()

  useEffect(() => {
    const onPagination$$ = onPagination$.subscribe((page) => {
      setPage(page)
    })

    return () => onPagination$$.unsubscribe()
  }, [onPagination$])

  useEffect(() => {
    const search$$ = search$.pipe(debounceTime(400)).subscribe(() => {
      dispatch(
        Actions[orderSlice.name].getAllOrderRequest({
          size: parseInt(take$.value),
          page: page - 1,
          filters: [
            {
              value: search$.value,
              parameter: 'name',
              filterType: 'SUBSTR'
            },
            ...(onlyCompleted
              ? [
                  {
                    value: '',
                    parameter: 'orderDone',
                    filterType: FilterTypes.NOT_NULL.key
                  }
                ]
              : []),
            ...(onlySubmitted
              ? [
                  {
                    value: '',
                    parameter: 'orderSubmission',
                    filterType: FilterTypes.NOT_NULL.key
                  }
                ]
              : []),
            ...(taxaFilter
              ? [
                  {
                    value: taxaFilter,
                    parameter: 'targetTaxonomy',
                    filterType: FilterTypes.SUBSTR.key
                  }
                ]
              : []),
            ...(processingTypeFilter
              ? [
                  {
                    value: processingTypeFilter,
                    parameter: 'processingType',
                    filterType: FilterTypes.SUBSTR.key
                  }
                ]
              : [])
          ]
        })
      )
    })
    const take$$ = take$.subscribe(() => {
      dispatch(
        Actions[orderSlice.name].getAllOrderRequest({
          size: parseInt(take$.value),
          page: page - 1,
          filters: [
            {
              value: search$.value,
              parameter: 'name',
              filterType: 'SUBSTR'
            },
            ...(onlyCompleted
              ? [
                  {
                    value: '',
                    parameter: 'orderDone',
                    filterType: FilterTypes.NOT_NULL.key
                  }
                ]
              : []),
            ...(onlySubmitted
              ? [
                  {
                    value: '',
                    parameter: 'orderSubmission',
                    filterType: FilterTypes.NOT_NULL.key
                  }
                ]
              : []),
            ...(taxaFilter
              ? [
                  {
                    value: taxaFilter,
                    parameter: 'targetTaxonomy',
                    filterType: FilterTypes.SUBSTR.key
                  }
                ]
              : []),
            ...(processingTypeFilter
              ? [
                  {
                    value: processingTypeFilter,
                    parameter: 'processingType',
                    filterType: FilterTypes.SUBSTR.key
                  }
                ]
              : [])
          ]
        })
      )
    })

    return () => {
      search$$.unsubscribe()
      take$$.unsubscribe()
    }
  }, [dispatch, take$, page, search$, onlyCompleted, onlySubmitted, taxaFilter, processingTypeFilter])

  return (
    <div>
      <div className="flex items-center space-x-4">
        <div className="w-full">
          <FormControl>
            <Input dataTestId={'order-search'} type="text" onChange$={search$} placeholder={'Search by Keyword '} />
          </FormControl>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={taxaFilter ?? 'NO_FILTER'}
            onChange={(e) => {
              if (e.target.value === 'NO_FILTER') {
                setTaxaFilter(null)
              } else {
                setTaxaFilter(e.target.value)
              }
              setPage(1)
            }}
            className="w-full p-2 border rounded-sm text-sm"
          >
            <option value="NO_FILTER">No Filter</option>
            {taxaOptions.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={processingTypeFilter ?? 'NO_FILTER'}
            onChange={(e) => {
              if (e.target.value === 'NO_FILTER') {
                setProcessingTypeFilter(null)
              } else {
                setProcessingTypeFilter(e.target.value)
              }
              setPage(1)
            }}
            className="w-full p-2 border rounded-sm text-sm"
          >
            <option value="NO_FILTER">Processing</option>
            <option value="METABARCODING">Metabarcoding</option>
            <option value="EDNA">eDNA</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="toggleOnlyCompleted"
            onChange={() => setOnlyCompleted((previous) => !previous)}
            data-testid="showOnlyCompletedCheckbox"
          />
          <label htmlFor="toggleOnlyCompleted" className="text-sm">
            Filter completed
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="toggleOnlySubmitted"
            onChange={() => setOnlySubmitted((previous) => !previous)}
            data-testid="showOnlySubmittedCheckbox"
          />
          <label htmlFor="toggleOnlySubmitted" className="text-sm">
            Filter submitted
          </label>
        </div>
        <div>
          <FormControl>
            <Select onChange$={take$} options={takeOptions} initialValue={take$.value} />
          </FormControl>
        </div>
      </div>
      <div className="flex flex-col p-4 overflow-y-auto max-h-64">
        {orders.map((order, index) => (
          <div
            onClick={() => onSelect(order.order)}
            className={'cursor-pointer p-3 bg-gray-100 rounded-lg shadow-md flex flex-row items-center justify-between gap-3'}
            key={index}
          >
            <div className="flex items-center gap-2">
              <span>
                Order {order.order.name} ({order.sampleCount} samples)
              </span>
              <ProcessingTypeBadge processingType={order.order.processingType} />
            </div>
            <span className={'ml-2'}>{getIconFromTaxa(order.order.targetTaxonomy)}</span>
          </div>
        ))}
      </div>

      <Pagination onClick$={onPagination$} page={page} total={total} take={parseInt(take$.value)}></Pagination>
    </div>
  )
}
